import { useCallback, useEffect, useMemo, useState } from "react";
import { InputNumber, Popover, Space, Table, Tooltip, Typography } from "antd";
import { EditOutlined, WarningFilled } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  getLeaveBalanceHistoryByEmployee,
  updateLeaveBalanceHistoryCell,
} from "../../utils/generalConfigurationService";
import { EmployeeLeaveBalanceHistory, YearlyBalanceCell } from "../../utils/types";
import { hasRoleAccess, Notify } from "../../utils/constants";
import { useUser } from "../../context/UserContext";
import {
  activeColumn,
  DEFAULT_EMPLOYEE_STATUS_FILTER,
  EmployeeStatusFilter,
  EmployeeStatusFilterValue,
  matchesEmployeeStatus,
} from "../Filters/EmployeeStatusFilter";

type YearCellProps = {
  cell?: YearlyBalanceCell;
  canEdit: boolean;
  onSave: (unusedDays: number) => Promise<void>;
};

const YearCell = ({ cell, canEdit, onSave }: YearCellProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<number>(cell?.unusedDays ?? 0);
  const [saving, setSaving] = useState(false);

  if (!cell) {
    return <span>-</span>;
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(value);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div style={{ minWidth: 220 }}>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Frozen on {dayjs(cell.frozenAt).format("DD/MM/YYYY HH:mm")}
        {cell.manuallyEdited && " · manually edited"}
      </Typography.Paragraph>
      {cell.hasDiscrepancy && (
        <Typography.Paragraph type="warning" style={{ marginBottom: 8 }}>
          Current leave data shows {cell.liveUnusedDays} unused days for this
          year - it no longer matches the frozen value.
        </Typography.Paragraph>
      )}
      <Space>
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => setValue(val ?? 0)}
        />
        <a onClick={handleSave} aria-disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </a>
      </Space>
    </div>
  );

  const cellText = (
    <span
      onClick={() => canEdit && setOpen(true)}
      style={{ cursor: canEdit ? "pointer" : "default" }}
    >
      {cell.unusedDays}
      {canEdit && <EditOutlined style={{ marginLeft: 6, opacity: 0.4 }} />}
    </span>
  );

  return (
    <Space size={4}>
      {canEdit ? (
        <Popover
          title="Edit unused days"
          trigger="click"
          open={open}
          onOpenChange={setOpen}
          content={content}
        >
          {cellText}
        </Popover>
      ) : (
        cellText
      )}
      {cell.hasDiscrepancy && (
        <Tooltip
          title={`Current leave data shows ${cell.liveUnusedDays} unused days - differs from the frozen value.`}
        >
          <WarningFilled style={{ color: "#faad14" }} />
        </Tooltip>
      )}
    </Space>
  );
};

const HistoryBalanceSection = () => {
  const { roles } = useUser();
  const isAdmin = hasRoleAccess(roles, ["ADMIN"]);

  const [years, setYears] = useState<number[]>([]);
  const [employees, setEmployees] = useState<EmployeeLeaveBalanceHistory[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [employeeStatusFilter, setEmployeeStatusFilter] =
    useState<EmployeeStatusFilterValue>(DEFAULT_EMPLOYEE_STATUS_FILTER);
  const visibleEmployees = useMemo(
    () =>
      employees.filter((employee) =>
        matchesEmployeeStatus(employee.employeeStatus, employeeStatusFilter),
      ),
    [employees, employeeStatusFilter],
  );

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLeaveBalanceHistoryByEmployee();
      setYears(data.years);
      setEmployees(data.employees);
    } catch (error) {
      console.error("Failed to load leave balance history:", error);
      Notify("error", "Could not load leave balance history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleSaveCell = async (
    record: EmployeeLeaveBalanceHistory,
    year: number,
    unusedDays: number,
  ) => {
    try {
      const data = await updateLeaveBalanceHistoryCell(
        record.employeeId,
        year,
        unusedDays,
        record.employmentPeriodId,
      );
      setYears(data.years);
      setEmployees(data.employees);
      Notify("success", "Unused days updated.");
    } catch (error) {
      console.error("Failed to update leave balance history cell:", error);
      Notify("error", "Could not update unused days.");
    }
  };

  const columns: ColumnsType<EmployeeLeaveBalanceHistory> = [
    {
      title: "Employee",
      dataIndex: "employeeName",
      key: "employeeName",
      fixed: "left",
      sorter: (a, b) => a.employeeName.localeCompare(b.employeeName),
      defaultSortOrder: "ascend",
    },
    {
      // One row per employment: a re-hired person has one row per employment.
      title: "Employee Code",
      key: "employeeCode",
      render: (_: unknown, record: EmployeeLeaveBalanceHistory) => (
        <Tooltip
          title={`${record.companyName ?? ""} ${
            record.periodStartDate
              ? dayjs(record.periodStartDate).format("DD.MM.YYYY")
              : ""
          } - ${
            record.periodEndDate
              ? dayjs(record.periodEndDate).format("DD.MM.YYYY")
              : "present"
          }`}
        >
          {record.employeeCode ?? "-"}
        </Tooltip>
      ),
    },
    activeColumn<EmployeeLeaveBalanceHistory>(),
    ...years.map((year) => ({
      title: String(year),
      key: String(year),
      align: "center" as const,
      render: (_: unknown, record: EmployeeLeaveBalanceHistory) => (
        <YearCell
          cell={record.cellsByYear[String(year)]}
          canEdit={isAdmin}
          onSave={(unusedDays) => handleSaveCell(record, year, unusedDays)}
        />
      ),
    })),
  ];

  return (
    <div className="section-container" style={{ marginTop: 16 }}>
      <Typography.Title level={4}>History balance</Typography.Title>
      <Typography.Paragraph type="secondary">
        Unused annual leave days each employee ended every closed calendar
        year with. Values are frozen once the year closes and only change if
        an admin corrects them.
        {isAdmin && " Click a value to edit it."}
      </Typography.Paragraph>
      <EmployeeStatusFilter
        className="mb-3"
        value={employeeStatusFilter}
        onChange={setEmployeeStatusFilter}
      />
      <Table
        rowKey={(record) => `${record.employmentPeriodId ?? record.employeeId}`}
        columns={columns}
        dataSource={visibleEmployees}
        loading={loading}
        scroll={{ x: true }}
        pagination={false}
        locale={{ emptyText: "No history available yet." }}
      />
    </div>
  );
};

export default HistoryBalanceSection;
